/**
 * ヘルプダイアログ
 */
export class HelpDialog {
  private container: HTMLElement
  private isVisible: boolean = false
  
  constructor() {
    this.container = this.createContainer()
    this.setupStyles()
    this.setupHelpButton()
  }
  
  /**
   * コンテナを作成
   */
  private createContainer(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'help-dialog'
    container.innerHTML = `
      <div class="help-dialog__backdrop"></div>
      <div class="help-dialog__content">
        <div class="help-dialog__header">
          <h2 class="help-dialog__title">ヘルプ</h2>
          <button class="help-dialog__close">×</button>
        </div>
        <div class="help-dialog__body">
          ${this.getHelpContent()}
        </div>
      </div>
    `
    
    document.body.appendChild(container)
    
    // イベントリスナー
    const backdrop = container.querySelector('.help-dialog__backdrop')
    backdrop?.addEventListener('click', () => this.hide())
    
    const closeBtn = container.querySelector('.help-dialog__close')
    closeBtn?.addEventListener('click', () => this.hide())
    
    return container
  }
  
  /**
   * ヘルプボタンを設定
   */
  private setupHelpButton(): void {
    const header = document.querySelector('.header')
    if (header) {
      const helpButton = document.createElement('button')
      helpButton.className = 'btn btn--secondary help-button'
      helpButton.innerHTML = `
        <span class="btn__icon">❓</span>
        ヘルプ
      `
      helpButton.addEventListener('click', () => this.show())
      
      const controls = header.querySelector('.header__controls')
      if (controls) {
        controls.appendChild(helpButton)
      }
    }
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .help-button {
        margin-left: 1rem;
      }
      
      .help-dialog {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9998;
        display: none;
      }
      
      .help-dialog.visible {
        display: block;
      }
      
      .help-dialog__backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        animation: fadeIn 0.3s ease;
      }
      
      .help-dialog__content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 90%;
        max-width: 700px;
        max-height: 80vh;
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
        display: flex;
        flex-direction: column;
        animation: slideUp 0.3s ease;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translate(-50%, -40%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }
      
      .help-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.5rem;
        border-bottom: 1px solid var(--border-color);
      }
      
      .help-dialog__title {
        margin: 0;
        font-size: 1.5rem;
        color: var(--text-primary);
      }
      
      .help-dialog__close {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.375rem;
        transition: all 0.2s ease;
      }
      
      .help-dialog__close:hover {
        background: #f3f4f6;
        color: var(--text-primary);
      }
      
      .help-dialog__body {
        padding: 1.5rem;
        overflow-y: auto;
        flex: 1;
      }
      
      .help-section {
        margin-bottom: 2rem;
      }
      
      .help-section:last-child {
        margin-bottom: 0;
      }
      
      .help-section__title {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 0.75rem;
      }
      
      .help-section__content {
        font-size: 0.875rem;
        color: var(--text-secondary);
        line-height: 1.6;
      }
      
      .help-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      
      .help-list__item {
        padding: 0.5rem 0;
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
      }
      
      .help-list__icon {
        flex-shrink: 0;
        font-size: 1rem;
      }
      
      .help-list__text {
        flex: 1;
      }
      
      .help-code {
        background: #f9fafb;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.8125rem;
        color: #374151;
      }
      
      .help-shortcut {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        background: #e5e7eb;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: #374151;
      }
      
      .help-table {
        width: 100%;
        margin-top: 0.5rem;
        border-collapse: collapse;
      }
      
      .help-table th,
      .help-table td {
        padding: 0.5rem;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
      }
      
      .help-table th {
        font-weight: 600;
        color: var(--text-primary);
        background: #f9fafb;
      }
      
      @media (max-width: 768px) {
        .help-dialog__content {
          width: 95%;
          max-height: 90vh;
        }
        
        .help-dialog__body {
          padding: 1rem;
        }
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * ヘルプコンテンツを取得
   */
  private getHelpContent(): string {
    return `
      <div class="help-section">
        <h3 class="help-section__title">🎯 基本的な使い方</h3>
        <div class="help-section__content">
          <ol class="help-list">
            <li class="help-list__item">
              <span class="help-list__icon">1️⃣</span>
              <span class="help-list__text">
                エディターにC言語のコードを入力します。<span class="help-code">for</span>、
                <span class="help-code">while</span>、<span class="help-code">do-while</span>
                のループ構文を含むコードを書きましょう。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">2️⃣</span>
              <span class="help-list__text">
                実行ボタンをクリックするか、<span class="help-shortcut">Ctrl + Enter</span>
                で実行を開始します。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">3️⃣</span>
              <span class="help-list__text">
                変数の値やループの状態を観察しながら、プログラムの動作を理解しましょう。
              </span>
            </li>
          </ol>
        </div>
      </div>
      
      <div class="help-section">
        <h3 class="help-section__title">⌨️ キーボードショートカット</h3>
        <div class="help-section__content">
          <table class="help-table">
            <thead>
              <tr>
                <th>操作</th>
                <th>Windows/Linux</th>
                <th>Mac</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>実行/一時停止</td>
                <td><span class="help-shortcut">Ctrl + Enter</span></td>
                <td><span class="help-shortcut">Cmd + Enter</span></td>
              </tr>
              <tr>
                <td>ステップ実行</td>
                <td><span class="help-shortcut">Ctrl + .</span></td>
                <td><span class="help-shortcut">Cmd + .</span></td>
              </tr>
              <tr>
                <td>リセット</td>
                <td><span class="help-shortcut">Ctrl + R</span></td>
                <td><span class="help-shortcut">Cmd + R</span></td>
              </tr>
              <tr>
                <td>速度を上げる</td>
                <td><span class="help-shortcut">Shift + ↑</span></td>
                <td><span class="help-shortcut">Shift + ↑</span></td>
              </tr>
              <tr>
                <td>速度を下げる</td>
                <td><span class="help-shortcut">Shift + ↓</span></td>
                <td><span class="help-shortcut">Shift + ↓</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="help-section">
        <h3 class="help-section__title">🔍 機能説明</h3>
        <div class="help-section__content">
          <ul class="help-list">
            <li class="help-list__item">
              <span class="help-list__icon">📝</span>
              <span class="help-list__text">
                <strong>コードエディター</strong>: Monaco Editorを使用した高機能なエディター。
                シンタックスハイライト、オートコンプリート、エラー表示機能付き。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">📊</span>
              <span class="help-list__text">
                <strong>変数パネル</strong>: 実行中の変数の値をリアルタイム表示。
                ループ変数は青色でハイライトされます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">🔄</span>
              <span class="help-list__text">
                <strong>ループ条件パネル</strong>: ループの条件式とその評価結果を表示。
                各イテレーションでの条件判定を確認できます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">🎨</span>
              <span class="help-list__text">
                <strong>ループフロー可視化</strong>: Canvas APIを使用したビジュアル表現。
                ネストしたループも階層的に表示されます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">⚡</span>
              <span class="help-list__text">
                <strong>速度制御</strong>: 実行速度を10段階で調整可能。
                プリセット（遅い、通常、速い、最速）も利用できます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">🔴</span>
              <span class="help-list__text">
                <strong>ブレークポイント</strong>: エディターの左端をクリックして
                ブレークポイントを設定できます。
              </span>
            </li>
          </ul>
        </div>
      </div>
      
      <div class="help-section">
        <h3 class="help-section__title">💡 サポートされている構文</h3>
        <div class="help-section__content">
          <ul class="help-list">
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                <span class="help-code">for (init; condition; update) { ... }</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                <span class="help-code">while (condition) { ... }</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                <span class="help-code">do { ... } while (condition);</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                変数宣言: <span class="help-code">int</span>、
                <span class="help-code">float</span>、
                <span class="help-code">char</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                演算子: <span class="help-code">+</span>、<span class="help-code">-</span>、
                <span class="help-code">*</span>、<span class="help-code">/</span>、
                <span class="help-code">%</span>、<span class="help-code">++</span>、
                <span class="help-code">--</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                比較演算子: <span class="help-code"><</span>、<span class="help-code">></span>、
                <span class="help-code"><=</span>、<span class="help-code">>=</span>、
                <span class="help-code">==</span>、<span class="help-code">!=</span>
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">✅</span>
              <span class="help-list__text">
                論理演算子: <span class="help-code">&&</span>、<span class="help-code">||</span>、
                <span class="help-code">!</span>
              </span>
            </li>
          </ul>
        </div>
      </div>
      
      <div class="help-section">
        <h3 class="help-section__title">🚀 ヒント</h3>
        <div class="help-section__content">
          <ul class="help-list">
            <li class="help-list__item">
              <span class="help-list__icon">💡</span>
              <span class="help-list__text">
                初めての方は「サンプル」ボタンから初級レベルのコードを選んで試してみましょう。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">💡</span>
              <span class="help-list__text">
                ステップ実行を使うと、ループの動作を1行ずつ確認できます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">💡</span>
              <span class="help-list__text">
                実行速度を遅くすると、変数の変化をじっくり観察できます。
              </span>
            </li>
            <li class="help-list__item">
              <span class="help-list__icon">💡</span>
              <span class="help-list__text">
                エラーが発生した場合は、エラーパネルの説明を読んで修正してください。
              </span>
            </li>
          </ul>
        </div>
      </div>
    `
  }
  
  /**
   * ダイアログを表示
   */
  show(): void {
    this.isVisible = true
    this.container.classList.add('visible')
  }
  
  /**
   * ダイアログを非表示
   */
  hide(): void {
    this.isVisible = false
    this.container.classList.remove('visible')
  }
}